import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "iuvity-ui-components/dist/column";
import { FormSkeleton, Header } from "../../components";
import { TabView } from "iuvity-ui-components/dist/tabview";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { PropertiesInfo } from "../../components/shared/PropertiesInfo";
import { ProdConfigsInfo } from "../../components/products/ProdConfigsInfo";
import { useProducts } from "../../hooks/useProducts";
import { useConfigurations } from "../../hooks/useConfigurations";
import { useProperties } from "../../hooks/useProperties";
import { useVersions } from "../../hooks/useVersions";
import { Product } from "../../interfaces";
import { AppContext } from "../../context/index";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

export const AdminProductPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { user, language, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const { data: productsData, productAction, reloadFn } = useProducts("ALL");

  const {
    data: propertiesData,
    propertyAction,
    reloadFn: propertyReloadFn,
  } = useProperties("DATA", "product");
  const { data: configurationsData, configurationAction } = useConfigurations(
    "DATA",
    "products"
  );
  const { data: versionsData } = useVersions("DATA");

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("Products.nameLabel"),
        },
        {
          field: "nameinapi",
          header: t("Products.nameinapiLabel"),
        },
        {
          field: "labelkey",
          header: t("Products.labelkeyLabel"),
        },
        {
          field: "hastransactions",
          header: t("Products.transactionsLabel"),
        },
        {
          field: "status",
          header: t("Products.statusLabel"),
        },
        {
          field: "releaseversion",
          header: t("Products.releaseversionLabel"),
        },
        {
          field: "actions",
          header: t("Products.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onEditAction = (product: Product) => {
    setAppState((prev) => ({
      ...prev,
      selectedProduct: product,
    }));
    sessionStorage.removeItem("PRODUCT-FV");
    navigate("/products/configuration");
  };

  const onDeleteAction = async (product: Product) => {
    await productAction("DELETE", product.id);
    reloadFn();
  };

  const handleModalProps = async (product: Product) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Products.deleteModalTitle"),
      content: `${t("Products.deleteModalContent1")} "${product?.name}" ${t(
        "Products.deleteModalContent2"
      )}`,
      onConfirm: () => onDeleteAction(product),
    }));
  };

  const formOptions = useMemo(() => {
    if (productsData && versionsData) {
      return {
        names: productsData
          .map((product) => ({
            label: product?.name,
            value: product?.name,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        versions: versionsData
          .map((version: any) => ({
            label: version?.name,
            value: version?.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
      };
    } else {
      return;
    }
  }, [productsData, versionsData]);

  useEffect(() => {
    if (configurationsData) {
      const formValues = configurationsData.reduce(
        (acc: any, current: any) => ({
          ...acc,
          [current.key.split(".").pop()]: current.value,
        }),
        {}
      );
      form.reset(formValues);
    }
  }, [configurationsData, form]);

  useEffect(() => {
    if (language) {
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(
            `Products.${col.field}Label`
          ),
        }))
      );
    }
  }, [t, language, setSelectedColumns]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Products.productAdminTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Products.productAdminTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Products.productAdminTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Products.adminDescriptionLabel")}</span>
        </div>
      </TransactionalSubheader>

      <BoxFull className="my-4 px-0 py-0" variant="tertiary">
        <TabView
          className="upload-tabview mt-4"
          activeIndex={activeIndex}
          onTabChange={(e) => {
            setActiveIndex(e.index);
            if (e.index === 0 && form.formState.isDirty) form.reset();
            setAppState((prev) => ({
              ...prev,
              functionalityCode: e.index === 1
                ? '/products/properties'
                : (e.index === 2
                  ? '/products/cross_configs'
                  : '/products'),
            }));
          }}
          items={[
            {
              key: "1",
              header: t("Products.productTypesTitle"),
              children: (
                <>
                  <SubheaderActions
                    className="px-0 pt-4 pb-3"
                    actions={subheaderActionsPrivilegeValidator([
                      {
                        id: "111",
                        icon: "iu iu-icon-plus",
                        label: t("Products.newProductBtnLabel"),
                        style: {
                          display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                        },
                        onClick: () => navigate(`${location.pathname}/new`),
                      },
                    ])}
                  />
                  <BoxFull className="dt-c px-0 py-0 mx-0" variant="tertiary">
                    {productsData ? (
                      <DataTable
                        header={
                          <TableHeader
                            title={t("Products.productTypesTitle")}
                            columns={columns}
                            selectedColumns={selectedColumns}
                            toggleColumns={setSelectedColumns}
                            filters={filters}
                            setFilters={setFilters}
                            reload={reloadFn}
                          />
                        }
                        currentPageReportTemplate={t(
                          "ConsoleTranslations.rowsPaginatorTemplateLabel"
                        )}
                        paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
                        emptyMessage={t("TableActions.emptyMessage")}
                        rows={10}
                        size="normal"
                        filters={filters}
                        sortField="id"
                        dataKey="id"
                        sortOrder={1}
                        value={productsData.map((product) => ({
                          ...product,
                          hastransactions: product.hastransactions
                            ? t("Products.enabledLabel")
                            : t("Products.disabledLabel"),
                          actions: (
                            <Actions
                              enableDelete={!!product.createdby}
                              onEdit={() => onEditAction(product)}
                              onDelete={() => handleModalProps(product)}
                              visibleObject={{...permissiondInfoByRole}}
                            />
                          ),
                        }))}
                        scrollable
                        resizableColumns
                      >
                        {selectedColumns.map((col) => {
                          return (
                            <Column
                              key={col.field}
                              field={col.field}
                              header={col.header}
                              sortable={!(col.field === "actions")}
                              align={
                                col.field === "actions" ? "center" : "left"
                              }
                              headerStyle={col.field === "actions"
                                ? {
                                    justifyContent: "center",
                                    display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                                  }
                                : undefined }
                              style={{
                                boxShadow: (col.field === "actions")
                                  ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                                  : undefined,
                                maxWidth: (col.field === "actions") ? "132px" : undefined,
                                whiteSpace: "normal",
                                minWidth: (col.field !== "actions") ? "10rem" : "",
                                display: (col.field === "actions")
                                  ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                                  : undefined,
                              }}
                              frozen={col.field === "actions" ? true : false}
                              alignFrozen={col.field === "actions" ? "right" : undefined}
                            />
                          );
                        })}
                      </DataTable>
                    ) : (
                      <FormSkeleton length={8} />
                    )}
                  </BoxFull>
                </>
              ),
              disabled: false,
            },
            {
              key: "2",
              header: t("Products.productPropertiesAdminTitle"),
              children: (
                <PropertiesInfo
                  title={t("Products.productPropertiesAdminTitle")}
                  data={propertiesData}
                  options={formOptions}
                  actionFn={propertyAction}
                  reloadFn={propertyReloadFn}
                  type="product"
                  auxModalController={setModalProps}
                />
              ),
            },
            {
              key: "3",
              header: t("Products.configurationsTabTitle"),
              children: configurationsData ? (
                <ProdConfigsInfo
                  data={configurationsData}
                  form={form}
                  fieldActionFn={configurationAction}
                  auxModalController={setModalProps}
                  setActiveIndex={setActiveIndex}
                />
              ) : (
                <FormSkeleton length={8} />
              ),
              disabled: false,
            },
          ]}
        />
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
